/**
 * Utility functions to prepare objects for Firestore insertion,
 * removing or normalizing `undefined` fields that would otherwise
 * cause Firestore's setDoc to crash.
 */

/**
 * Recursively find and return an array of paths that have `undefined` values.
 * Useful for debugging before passing to the sanitizer.
 */
export function findUndefinedPaths(obj: any, path: string = ''): string[] {
  let paths: string[] = [];
  
  if (obj === undefined) {
    paths.push(path || 'root');
    return paths;
  }
  
  if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
    return paths;
  }

  // Handle Firebase Timestamps (they have seconds and nanoseconds)
  if (obj.toDate && typeof obj.toDate === 'function') {
    return paths;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const currentPath = path ? `${path}[${index}]` : `[${index}]`;
      paths = paths.concat(findUndefinedPaths(item, currentPath));
    });
  } else {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const currentPath = path ? `${path}.${key}` : key;
        paths = paths.concat(findUndefinedPaths(obj[key], currentPath));
      }
    }
  }

  return paths;
}

/**
 * Recursively sanitize an object for Firestore.
 * - `undefined` object properties are omitted (deleted).
 * - `undefined` array items are converted to `null` to preserve index matching.
 * - Primitive values, Dates, Timestamps, and `null` are preserved.
 */
export function sanitizeForFirestore(obj: any): any {
  if (obj === undefined) {
    return null; // Top-level undefined becomes null just in case
  }
  
  if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
    return obj;
  }

  // Preserve Firestore Timestamp objects
  if (obj.toDate && typeof obj.toDate === 'function') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => item === undefined ? null : sanitizeForFirestore(item));
  }

  const sanitized: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined) {
        sanitized[key] = sanitizeForFirestore(value);
      }
    }
  }
  return sanitized;
}
